import React, { useState } from 'react';
import { BookOpen, ChevronDown, Film, Globe, Headphones } from 'lucide-react';
import {
  KnowledgeModule,
  TrainingMedia,
  projectKnowledge,
  sampleCalls,
  shortVideos,
  websiteFacts,
  websiteUrl,
} from '../content/training';

/** Training & resources — the static bundle the Nurture app carries
 *  (TrainingScreen.kt): the AI agent's knowledge modules, website facts,
 *  sample calls and short videos. Nothing is fetched; content lives in
 *  `content/training.ts`, a lockstep port of the app's TrainingContent.kt.
 */

const MediaFigure: React.FC<{ media: TrainingMedia }> = ({ media }) => (
  <figure className="mt-3 m-0">
    {media.type === 'image'
      ? <img src={media.url} alt={media.caption ?? ''} loading="lazy" className="w-full rounded-lg" />
      : <video src={media.url} controls preload="none" className="w-full rounded-lg bg-charcoal" />}
    {media.caption && <figcaption className="text-[12px] text-graphite mt-1">{media.caption}</figcaption>}
  </figure>
);

const ModuleRow: React.FC<{ module: KnowledgeModule }> = ({ module }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-dotted border-sketch-soft last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-start gap-3 py-2.5 text-left"
      >
        <span className="flex-1">
          <span className="block font-semibold text-[14px]">{module.order}. {module.title}</span>
          <span className="block text-[12.5px] text-graphite mt-0.5">{module.summary}</span>
        </span>
        <ChevronDown size={16} className={`text-graphite mt-0.5 flex-none transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="pb-3">
          <div className="whitespace-pre-wrap text-[13.5px] leading-relaxed">{module.body}</div>
          {module.media.map((m) => <MediaFigure key={m.url} media={m} />)}
        </div>
      )}
    </div>
  );
};

const Count: React.FC<{ n: number }> = ({ n }) => <span className="chip chip-mute ml-auto num">{n}</span>;

const TrainingPage: React.FC = () => (
  <div className="w-full px-7 pt-6 pb-16">
    <div className="page-head">
      <div>
        <h1 className="page-title">Training &amp; resources</h1>
        <p className="page-sub">The same bundle the Nurture app carries</p>
      </div>
    </div>

    <div className="grid gap-4 lg:grid-cols-2 items-start">
      <section className="card">
        <h2 className="card-h"><BookOpen size={14} /> Project knowledge <Count n={projectKnowledge.length} /></h2>
        <p className="page-sub -mt-2 mb-1">What the AI agent shares with leads</p>
        {projectKnowledge.map((m) => <ModuleRow key={m.id} module={m} />)}
      </section>

      <div className="space-y-4">
        <section className="card">
          <h2 className="card-h"><Globe size={14} /> Project website</h2>
          {websiteFacts.map((f) => (
            <div key={f.label} className="kv"><span>{f.label}</span><span>{f.value}</span></div>
          ))}
          <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="btn btn-pri inline-block mt-3">
            Open website ↗
          </a>
        </section>

        <section className="card">
          <h2 className="card-h"><Headphones size={14} /> Sample calls <Count n={sampleCalls.length} /></h2>
          <p className="page-sub -mt-2 mb-1">Real calls to listen and learn from</p>
          {sampleCalls.length === 0
            ? <p className="text-[13px] text-graphite">No sample calls added yet.</p>
            : sampleCalls.map((c) => (
              <div key={c.id} className="work flex-col items-stretch gap-1.5">
                <span className="font-semibold text-[14px]">{c.title}</span>
                <audio src={c.recording.url} controls preload="none" className="w-full" />
              </div>
            ))}
        </section>

        <section className="card">
          <h2 className="card-h"><Film size={14} /> Short videos <Count n={shortVideos.length} /></h2>
          <p className="page-sub -mt-2 mb-3">Quick walkthroughs to share &amp; learn</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {shortVideos.map((v) => (
              <div key={v.id} className="border-[1.5px] border-sketch rounded-lg overflow-hidden bg-cream">
                <video src={v.url} controls preload="none" className="w-full bg-charcoal" />
                <div className="px-3 py-2">
                  <div className="font-semibold text-[13.5px]">{v.title}</div>
                  <div className="text-[12px] text-graphite mt-0.5">{v.description}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  </div>
);

export default TrainingPage;
